/**
 * One subsection's table card: the glyph + heading, one column header row,
 * one functional row per operation (`659:19671`/`659:19825`). Every
 * subsection on every tab shares this one component -- Mídia
 * has no Figma frame of its own (`spec/figma/659-19671`, `659-19825`), so it
 * reuses the pattern the two drawn tabs already prove rather than inventing
 * a second one.
 */
import { useMemo, useState, type ReactNode } from 'react';

import { failureSentence } from '../../chrome/state';
import { Absent } from '../../chrome/measure';
import { getLocale } from '../../i18n';
import { credits as formatCredits } from '../../i18n/format';
import { ApiFailureError, type ApiFailure } from '../../lib/http/errors';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import { useOpsKeys, useSaveOperation } from '../api/queries';
import type { OperationBasis, OperationCatalogueRow, OperationProvider } from '../api/schemas';
import { InferenceRow } from './InferenceRow';
import { operationNameOf, t, taskOf } from './messages';

export interface Subsection {
  id: string;
  heading: string;
  icon: ReactNode;
  rows: OperationCatalogueRow[];
}

const TOKEN_METERED: ReadonlySet<OperationProvider> = new Set([
  'openrouter',
  'openai',
  'google_ai',
  'anthropic',
]);

type UnitKind = 'page' | 'profile' | 'item' | 'tokensIn' | 'tokensOut';

function defaultBasisKind(provider: OperationProvider): 'unit' | 'tokens' {
  return TOKEN_METERED.has(provider) ? 'tokens' : 'unit';
}

/** The provider fixes the unit in code (`core`'s `PROVIDER_UNITS`); which
 *  word names it is copy this screen owns. A SocialCrawl sweep prices per
 *  profile visited, not per page returned, so it is the one collection verb
 *  that reads `cr/perfil` rather than `cr/página` (`659:19671`). */
function unitKindsFor(provider: OperationProvider, operationKey: string): UnitKind[] {
  if (TOKEN_METERED.has(provider)) return ['tokensIn', 'tokensOut'];
  if (provider === 'platform') return ['item'];
  return [operationKey.endsWith('.sweep') ? 'profile' : 'page'];
}

interface Draft {
  provider: OperationProvider;
  keyId: string | null;
  basisValues: string[];
  multiplier: string;
}

function draftFromConfig(row: OperationCatalogueRow): Draft {
  const config = row.config;
  // `OPERATIONS` (`core`'s `services.pricing.operations`) never registers a
  // key with an empty provider set; the fallback only satisfies a type that
  // cannot see that invariant.
  const provider = config?.provider ?? row.providers[0] ?? 'platform';
  const kind = defaultBasisKind(provider);
  if (config === null) {
    return {
      provider,
      keyId: null,
      basisValues: kind === 'tokens' ? ['', ''] : [''],
      multiplier: '',
    };
  }
  const basisValues =
    config.basis.kind === 'tokens'
      ? [config.basis.credits_per_million_input, config.basis.credits_per_million_output]
      : [config.basis.credits_per_unit];
  return { provider, keyId: config.key?.id ?? null, basisValues, multiplier: config.multiplier };
}

/** Plain decimal notation only: `paysCredits` feeds the digits to `BigInt`,
 *  which throws on the exponents and spaces `Number` would accept. */
function isPositiveDecimal(value: string): boolean {
  return /^\d+(\.\d+)?$/.test(value) && Number(value) > 0;
}

/** Exact `ceil(basis * multiplier)` over the wire's decimal strings, in
 *  `BigInt` arithmetic rather than `Number`: the wire carries decimal
 *  strings exactly so a float multiply can't silently round a price
 *  (`"1.1" * "100"` must read `110`, never `111`). */
function paysCredits(basis: string, multiplier: string): number {
  const [bWhole, bFrac = ''] = basis.split('.');
  const [mWhole, mFrac = ''] = multiplier.split('.');
  const scale = bFrac.length + mFrac.length;
  const product = BigInt(bWhole + bFrac) * BigInt(mWhole + mFrac);
  const divisor = 10n ** BigInt(scale);
  return Number((product + divisor - 1n) / divisor);
}

function PaysCell(props: {
  unitKinds: UnitKind[];
  basisValues: string[];
  multiplier: string;
}): ReactNode {
  const locale = getLocale();
  const multiplierValid = isPositiveDecimal(props.multiplier);
  return (
    <div className="flex flex-col gap-1">
      {props.unitKinds.map((kind, index) => {
        const raw = props.basisValues[index] ?? '';
        const valid = multiplierValid && isPositiveDecimal(raw);
        return (
          <div key={kind} data-testid="pays" className="text-end">
            {valid ? (
              <>
                <div className="font-semibold">
                  {formatCredits(paysCredits(raw, props.multiplier), locale)} {t('pays.credits')}
                </div>
                <div className="text-caption text-muted-foreground">{t(`unit.${kind}.pays`)}</div>
              </>
            ) : (
              <Absent reason="not_measured" />
            )}
          </div>
        );
      })}
    </div>
  );
}

function OperationRow(props: { row: OperationCatalogueRow }): ReactNode {
  const { row } = props;

  const keysQuery = useOpsKeys();
  const save = useSaveOperation();

  const baseline = useMemo(() => draftFromConfig(row), [row]);
  const [draft, setDraft] = useState<Draft>(baseline);
  const [failure, setFailure] = useState<ApiFailure | null>(null);

  const basisKind = defaultBasisKind(draft.provider);
  const unitKinds = useMemo(
    () => unitKindsFor(draft.provider, row.operation_key),
    [draft.provider, row.operation_key],
  );
  const name = operationNameOf(row.operation_key);
  const task = taskOf(row.operation_key);
  // One row per provider shares a task's single inference row, so only a saved pairing -- one
  // with the key a keyed provider needs -- may carry it; an unpriced sibling would save its
  // provider with no key.
  const showInference =
    task !== null && row.config?.key != null && TOKEN_METERED.has(row.config.provider);
  const needsKey = draft.provider !== 'platform';

  const dirty =
    draft.provider !== baseline.provider ||
    draft.keyId !== baseline.keyId ||
    draft.multiplier !== baseline.multiplier ||
    draft.basisValues.some((value, index) => value !== baseline.basisValues[index]);

  const validBasis = draft.basisValues.every((value) => isPositiveDecimal(value));
  const validMultiplier = isPositiveDecimal(draft.multiplier);
  const canSave = dirty && validBasis && validMultiplier && (!needsKey || draft.keyId !== null);
  const missingKeyChip = needsKey && draft.keyId === null;

  function handleProviderChange(next: OperationProvider): void {
    const nextKind = defaultBasisKind(next);
    setDraft((d) => ({
      ...d,
      provider: next,
      // A metering-kind change (unit <-> tokens) invalidates the old basis
      // figures -- they price a different unit.
      basisValues:
        defaultBasisKind(d.provider) === nextKind
          ? d.basisValues
          : nextKind === 'tokens'
            ? ['', '']
            : [''],
      // A key authenticates to one provider, so switching the provider drops it.
      keyId: next === d.provider ? d.keyId : null,
    }));
  }

  function handleSave(): void {
    const basis: OperationBasis =
      basisKind === 'tokens'
        ? {
            kind: 'tokens',
            credits_per_million_input: draft.basisValues[0] ?? '',
            credits_per_million_output: draft.basisValues[1] ?? '',
          }
        : { kind: 'unit', credits_per_unit: draft.basisValues[0] ?? '' };
    setFailure(null);
    save.mutate(
      {
        operationKey: row.operation_key,
        provider: draft.provider,
        keyId: draft.keyId,
        basis,
        multiplier: draft.multiplier,
      },
      {
        onSuccess: (updated) => setDraft(draftFromConfig(updated)),
        onError: (error) => {
          setFailure(
            error instanceof ApiFailureError ? error.failure : { kind: 'server', status: 0 },
          );
        },
      },
    );
  }

  const activeKeys = (keysQuery.data ?? []).filter(
    (key) => key.state === 'ACTIVE' && key.provider === draft.provider,
  );
  const providerLabel = t(`provider.${draft.provider}` as const);
  const ariaFor = {
    providerCost:
      unitKinds.length > 1
        ? (index: number) =>
            t(index === 0 ? 'aria.providerCost.input' : 'aria.providerCost.output', {
              operation: name,
            })
        : () => t('aria.providerCost', { operation: name }),
  };

  return (
    <>
      <TableRow data-testid={`operation-${row.operation_key}`} data-key={row.operation_key}>
        <TableCell>
          <span className="font-medium">{name}</span>
        </TableCell>

        <TableCell>
          <Select
            value={draft.provider}
            onValueChange={(value) => handleProviderChange(value as OperationProvider)}
          >
            <SelectTrigger aria-label={t('aria.provider', { operation: name })} size="sm">
              <SelectValue>{providerLabel}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {row.providers.map((p) => (
                <SelectItem key={p} value={p}>
                  {t(`provider.${p}` as const)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </TableCell>

        <TableCell>
          {!needsKey ? (
            <Select value="none" disabled onValueChange={() => {}}>
              <SelectTrigger aria-label={t('aria.key', { operation: name })} size="sm">
                <SelectValue>{t('key.none')}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t('key.none')}</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <div className="flex flex-col gap-1">
              <Select
                value={draft.keyId ?? undefined}
                onValueChange={(value) => setDraft((d) => ({ ...d, keyId: value }))}
              >
                <SelectTrigger aria-label={t('aria.key', { operation: name })} size="sm">
                  <SelectValue placeholder={t('key.placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  {activeKeys.map((key) => (
                    <SelectItem key={key.id} value={key.id}>
                      {key.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {missingKeyChip && (
                <Badge variant="destructive" className="w-fit">
                  {t('key.missing.chip')}
                </Badge>
              )}
            </div>
          )}
        </TableCell>

        <TableCell>
          <div className="flex flex-col gap-1">
            {unitKinds.map((kind, index) => (
              <div key={kind} className="relative">
                <Input
                  type="number"
                  min={0}
                  step="any"
                  aria-label={ariaFor.providerCost(index)}
                  value={draft.basisValues[index] ?? ''}
                  onChange={(event) =>
                    setDraft((d) => {
                      const basisValues = [...d.basisValues];
                      basisValues[index] = event.target.value;
                      return { ...d, basisValues };
                    })
                  }
                  className="pr-16"
                />
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-caption text-muted-foreground"
                >
                  {t(`unit.${kind}.input`)}
                </span>
              </div>
            ))}
          </div>
        </TableCell>

        <TableCell>
          <Input
            type="number"
            min={0}
            step="any"
            aria-label={t('aria.multiplier', { operation: name })}
            value={draft.multiplier}
            onChange={(event) => setDraft((d) => ({ ...d, multiplier: event.target.value }))}
            className="w-16"
          />
        </TableCell>

        <TableCell>
          <PaysCell
            unitKinds={unitKinds}
            basisValues={draft.basisValues}
            multiplier={draft.multiplier}
          />
        </TableCell>

        <TableCell>
          {dirty && (
            <Button
              type="button"
              size="sm"
              disabled={!canSave || save.isPending}
              aria-label={t('aria.save', { operation: name })}
              onClick={handleSave}
            >
              {t('save')}
            </Button>
          )}
        </TableCell>
      </TableRow>

      {failure !== null && (
        <TableRow>
          <TableCell colSpan={7}>
            <p role="alert" className="text-caption text-destructive">
              {failure.kind === 'invalid' ? failure.message : failureSentence(failure)}
            </p>
          </TableCell>
        </TableRow>
      )}

      {showInference && task !== null && (
        <TableRow>
          <TableCell colSpan={7} className="p-0">
            <InferenceRow
              task={task}
              operationKey={row.operation_key}
              provider={baseline.provider}
              keyId={baseline.keyId}
            />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export function SubsectionTable(props: { subsection: Subsection }): ReactNode {
  const { subsection } = props;
  return (
    <Card>
      <CardContent className="flex flex-col gap-0 px-0">
        <div className="flex items-center gap-3 px-5 py-3">
          <div className="flex size-7 items-center justify-center rounded-md bg-muted">
            {subsection.icon}
          </div>
          <h3 className="text-title-sm font-semibold">{subsection.heading}</h3>
        </div>
        <Table label={subsection.heading}>
          <TableCaption className="sr-only">{subsection.heading}</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead>{t('header.operation')}</TableHead>
              <TableHead>{t('header.provider')}</TableHead>
              <TableHead>{t('header.key')}</TableHead>
              <TableHead>{t('header.providerCost')}</TableHead>
              <TableHead>{t('header.multiplier')}</TableHead>
              <TableHead className="text-end">{t('header.pays')}</TableHead>
              <TableHead>
                <span className="sr-only">{t('save')}</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {subsection.rows.map((row) => (
              <OperationRow key={row.operation_key} row={row} />
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
